import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Phone, CalendarCheck, AlertTriangle } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SiteFooter from '../components/SiteFooter';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('loading'); // loading | confirmed | failed | unpaid | error
  const [data, setData] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let active = true;
    const poll = async () => {
      tries.current += 1;
      try {
        const r = await fetch(`${API}/api/booking/status/${sessionId}`);
        const d = await r.json();
        if (!active) return;
        setData(d);
        if (d.status === 'confirmed') { setState('confirmed'); return; }
        if (d.status === 'paid_booking_failed') { setState('failed'); return; }
        if (tries.current > 20) { setState(d.payment_status === 'paid' ? 'failed' : 'unpaid'); return; }
        setTimeout(poll, 2500);
      } catch {
        if (active && tries.current > 20) setState('error');
        else if (active) setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) : '';

  return (
    <>
      <SEO title="Booking confirmation | Restore.Dental" path="/payment/success" noindex />
      <PageHeader />
      <main className="min-h-screen bg-gradient-to-br from-sky-gray/5 to-white pt-28 pb-20 px-4" data-testid="booking-success-page">
        <div className="container mx-auto max-w-xl">
          {state === 'loading' && (
            <div className="bg-white rounded-2xl border border-sky-gray/20 p-10 text-center" data-testid="booking-loading">
              <Loader2 className="w-10 h-10 text-sky-gray animate-spin mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-rabbit mb-2">Confirming your appointment…</h1>
              <p className="text-chocolate-malt/70">Please don't close this page — we're confirming your payment and booking you into Dr Patel's diary.</p>
            </div>
          )}

          {state === 'confirmed' && (
            <div className="bg-white rounded-2xl border border-sky-gray/20 p-8 md:p-10 text-center" data-testid="booking-confirmed">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="w-9 h-9 text-green-600" /></div>
              <h1 className="text-3xl font-bold text-rabbit mb-2">You're booked in 🎉</h1>
              <p className="text-chocolate-malt/75 mb-6">We've emailed your confirmation. Here are your details:</p>
              <div className="text-left bg-sky-gray/5 rounded-xl border border-sky-gray/20 p-5 space-y-2">
                <div className="flex items-center gap-2 text-rabbit font-bold"><CalendarCheck className="w-5 h-5 text-burnt-coral" />{data?.type_name}</div>
                <p className="text-rabbit">{fmt(data?.start_time)}</p>
                <p className="text-sm text-chocolate-malt/70">with {data?.practitioner_name} · Restore.Dental, 64a Horn Lane, Acton, London W3 6NP</p>
                <p className="text-sm text-chocolate-malt/70">Reference: <strong>{data?.reference}</strong> · Paid £{Number(data?.fee_gbp || 0).toFixed(0)}</p>
              </div>
              <Button asChild size="lg" className="mt-6 bg-burnt-coral hover:bg-burnt-coral/90 text-white"><Link to="/" data-testid="booking-home-link">Back to homepage</Link></Button>
            </div>
          )}

          {state === 'failed' && (
            <div className="bg-white rounded-2xl border border-amber-200 p-8 text-center" data-testid="booking-failed">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-5"><AlertTriangle className="w-9 h-9 text-amber-600" /></div>
              <h1 className="text-2xl font-bold text-rabbit mb-2">Payment received — we'll confirm by phone</h1>
              <p className="text-chocolate-malt/75 mb-4">Your payment went through (reference <strong>{data?.reference}</strong>), but we couldn't automatically lock in that exact slot. Our team will call you shortly to finalise your appointment — no need to pay again.</p>
              <a href="tel:+447787814064" className="inline-flex items-center gap-2 text-burnt-coral font-semibold"><Phone className="w-4 h-4" /> 07787 814 064</a>
            </div>
          )}

          {(state === 'unpaid' || state === 'error') && (
            <div className="bg-white rounded-2xl border border-sky-gray/20 p-8 text-center" data-testid="booking-unpaid">
              <h1 className="text-2xl font-bold text-rabbit mb-2">Payment not completed</h1>
              <p className="text-chocolate-malt/75 mb-6">It looks like the payment wasn't finished, so no appointment was booked. You can try again or call us.</p>
              <Button asChild size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white"><Link to="/book" data-testid="booking-retry-link">Try booking again</Link></Button>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </>
  );
}
